import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button, CloseIcon, Datepicker, DatepickerCalendar, DatepickerCalendarHeaderProps, Flex, ProviderConsumer, Text, pxToRem } from '@fluentui/react-northstar';
import i18next from 'i18next';
import { platformService } from '../../services/platform.service';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { getDropdownBoxShadow } from '../../../tikit/ticketHelper';

import { DatepickerLocalizations } from '../../../App';
import { CustomPropertyDataType_LABELS } from '../../../custom-properties/utils/constants';
import { formatTimeInput } from '../../../templates/CustomComponents/Utils/CustomElementUtils';
import { getCardDateInputISOFormatted, getCardTimeInputISOFormatted, getISODateOnlyPartString } from '../TicketTemplateAnswer';
import { ICustomProperty } from '../../interfaces/customproperties.interface';
import { PanelActions, focusActions, holdOutsideClick, isPanelAction, nudge, wrapActionTab } from './PanelActions';
import { CELL_BOX, PANEL_PORTAL, SAVE_ERROR_COLOR, SaveMark, SaveOutcome, markClass, reportFailure, savingCell, useCellTimers, useGreenOnReload, useSaveMark, focusCellBack } from './inlineCell';
import { InlineComboCell } from './InlineComboCell';
import { CategoryOption, buildCategoryOptions, optionName, withHeldOptions } from './inlineCategoryOptions';
import { isInTypeaheadList, usePanelPlacement } from './TypeaheadList';
import { MultiTypeaheadField } from './TypeaheadField';
import { ConvertDateToLocale } from '../../common/ConvertDate';
import { CustomPropertyResting, answerText } from './CustomPropertyResting';
import { DateTime } from 'luxon';
import { hoverTitle } from './emptyCell';
import { ClockGlyph } from '../../../custom-properties/CustomTimePicker';
import { getThemeMode } from '../../utils/helper';

/** Saves a value and says how that ended (see SaveOutcome). */
type Commit<T> = (value: T) => Promise<SaveOutcome>;

export { reportFailure };

/**
 * Wraps a save so that a refusal or failure is reported, and a ticket list reload follows a success.
 * `guard` runs first - My Work's rows cannot say whether their ticket is closed. It answers 'saved' as soon as
 * the server has the value, without waiting for the reload, which is most of the wait on a full page.
 */
export const committing = <T,>(save: (value: T) => Promise<void>, onSaved: () => void | Promise<void>, guard?: () => Promise<void>): Commit<T> =>
  async (value: T) => {
    try {
      await guard?.();
      await save(value);
    } catch (error: unknown) {
      return reportFailure(error, { reload: onSaved });
    }
    reloadAfterSave(onSaved);
    return 'saved';
  };

/** The reload after a save that has already landed - a failure here leaves the old rows, not an unsaved value. */
export const reloadAfterSave = (onSaved: () => void | Promise<void>) => {
  Promise.resolve().then(onSaved).catch((error: unknown) => console.error('Reload after an inline save failed', error));
};

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

// ---------------------------------------------------------------------------------------------------------------
// A box in the cell - text, number or time

interface TextCellProps {
  /** The ticket's (or row's) identity, so a reloaded row hands the cell its new value. */
  rowKey: number;
  /** As the box shows it: the text, the number, or "HH:mm". */
  value: string;
  inputType: 'text' | 'number' | 'time';
  label: string;
  /**
   * What the cell's save state (value being saved, green box, last-edited box) is kept under, by ticket - the column's
   * name unless given. A custom property passes its id: one named like a built-in column shared that column's state.
   */
  stateKey?: string;
  required?: boolean;
  resting: React.ReactElement;
  /** How the list draws a value in this column - what was typed is drawn so while its save is out. */
  restingFor?: (text: string) => React.ReactElement;
  commit: Commit<string>;
}

/**
 * A text, number or time box over the whole cell. Saves when the box is left - a click away, Tab or Enter -
 * and only if the value changed; Escape puts it back. A required field left blank keeps its saved value.
 */
export const InlineTextCell = ({ rowKey, value, inputType, label, stateKey, required, resting, restingFor, commit }: TextCellProps) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  const [pending, setPending] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const restingRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef(true);
  const cancelledRef = useRef(false);
  const refocusRef = useRef(false);
  const saveMark = useSaveMark(rowKey, value, saving, stateKey ?? label);

  useEffect(() => { setEditing(false); setPending(null); }, [rowKey]);
  useEffect(() => { setPending(null); }, [value]);

  useEffect(() => {
    if (editing) { inputRef.current?.focus(); if (inputType === 'text') inputRef.current?.select(); }
    else if (refocusRef.current) { refocusRef.current = false; focusCellBack(restingRef.current); }
  }, [editing]);

  const start = (withText?: string) => {
    leftRef.current = false;
    cancelledRef.current = false;
    setText(withText ?? pending ?? value);
    setEditing(true);
  };

  const leave = async () => {
    if (leftRef.current) return;
    leftRef.current = true;
    setEditing(false);
    if (cancelledRef.current || text === value || (required && !text.trim())) return;
    setPending(text);
    setSaving(true);
    const outcome = await commit(text);
    setSaving(false);
    saveMark.show(outcome);
    if (outcome === 'saved') return;
    setPending(null);
    // A failed save reopens the box with what was typed, boxed red, to try again or Escape.
    if (outcome === 'failed') start(text);
  };

  const byKeyboard = (cancel: boolean) => { cancelledRef.current = cancel; refocusRef.current = true; leave(); };

  // A required box left blank keeps the agent in it (Shaun, 2026-10-14): Tab, Enter and a click away flash it
  // and stay. Escape is the way out, back to what the ticket holds.
  const blankRequired = editing && !!required && !text.trim();
  const blankRef = useRef(blankRequired);
  blankRef.current = blankRequired;
  useEffect(() => {
    if (!blankRequired) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!blankRef.current || event.composedPath().includes(inputRef.current)) return;
      holdOutsideClick(event);
      nudge(inputRef.current);
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [blankRequired]);

  return (
    <ProviderConsumer
      render={globalTheme => {
        const { colorScheme } = globalTheme.siteVariables;
        const marked = markClass(saveMark.mark, saveMark.edited);
        const lock = savingCell(saveMark.busy);
        if (!editing) {
          let shown = resting;
          if (pending != null) shown = restingFor ? restingFor(pending) : <span className="truncate">{pending}</span>;
          return (
            <div
              ref={restingRef}
              className={`inline-edit-cell${marked}${lock.className}`}
              {...lock.props}
              data-selection-disabled={true}
              tabIndex={0}
              role="button"
              aria-label={`${label}: ${pending ?? value}`}
              onMouseDown={stop}
              onClick={event => { stop(event); saveMark.opening(); start(); }}
              onKeyDown={event => { if (event.key === 'Enter' || event.key === 'F2') { stop(event); event.preventDefault(); saveMark.opening(); start(); } }}
              style={{ ...CELL_BOX, cursor: 'text', ...saveMark.markStyle }}
            >
              {shown}
            </div>
          );
        }
        const invalid = (required && !text.trim()) || saveMark.mark === 'failed';
        const isTime = inputType === 'time';
        let borderColor = colorScheme.brand.foreground;
        if (saveMark.mark === 'failed') borderColor = SAVE_ERROR_COLOR;
        else if (invalid) borderColor = colorScheme.red.foreground;
        return (
          // A time box is drawn as the ticket page's (CustomTimePicker): the browser's clock hidden, the theme's in its place.
          // The box is absolute (CELL_BOX), so the time wrapper fills the cell too, or the clock centres on a 0px line.
          <div
            data-selection-disabled={true}
            onMouseDown={stop}
            onClick={stop}
            className={isTime ? 'time-container' : undefined}
            style={isTime ? { position: 'absolute', inset: 0 } : undefined}
          >
            <input
              className={isTime && getThemeMode() === 'dark' ? 'time-input' : undefined}
              ref={inputRef}
              type={inputType}
              data-inline-editor="true"
              min={inputType === 'number' ? 0 : undefined}
              aria-label={label}
              aria-invalid={invalid}
              value={text}
              onChange={event => setText(event.target.value)}
              onKeyDown={event => {
                event.stopPropagation();
                if (event.key === 'Enter' || event.key === 'Tab') { event.preventDefault(); if (blankRequired) nudge(inputRef.current); else byKeyboard(false); }
                else if (event.key === 'Escape') { event.preventDefault(); byKeyboard(true); }
              }}
              onBlur={leave}
              style={{
                ...CELL_BOX,
                width: '100%',
                // .time-container sets a time box 32px tall for the ticket page; in a cell it fills the row.
                height: isTime ? '100%' : undefined,
                margin: 0,
                font: 'inherit',
                paddingLeft: 11,
                outline: 'none',
                border: `1px solid ${borderColor}`,
                backgroundColor: colorScheme.default.background,
                color: colorScheme.default.foreground,
                ['--inline-nudge-color' as string]: colorScheme.red.foreground
              }}
            />
            {isTime && (
              <button
                type="button"
                className="time-icon"
                tabIndex={-1}
                aria-hidden="true"
                // Pressing it must not take focus from the box: leaving the box saves and closes the cell.
                onMouseDown={event => { stop(event); event.preventDefault(); }}
                onClick={event => {
                  stop(event);
                  try { inputRef.current?.showPicker(); } catch { /* no picker: the box is still typable */ }
                }}
              >
                <ClockGlyph />
              </button>
            )}
          </div>
        );
      }}
    />
  );
};

// ---------------------------------------------------------------------------------------------------------------
// A panel under the cell - the date calendar, and lists that take several values

interface CellPanelProps {
  label: string;
  resting: React.ReactElement;
  width: number;
  /** The cell's save is out (or its row is still reloading): it pulses and cannot be opened. */
  busy: boolean;
  mark: SaveMark;
  /** useSaveMark's markStyle: a green picked up part-way carries on. */
  markStyle?: React.CSSProperties;
  /** This cell was the last one saved on the page (useSaveMark). */
  edited?: boolean;
  /** Changing it reopens the panel - after a failed save, with what was entered still in it. */
  reopen?: number;
  /** Called as the panel closes: keep = left by a click away, Tab off its end or Enter; not = Escape. */
  onClose: (keep: boolean) => void;
  onOpen?: () => void;
  /**
   * Given for a panel of several picks: Save and Cancel under it, Save lit by canSave, and a click away closes
   * it only while nothing changed. Without it a panel is one pick (a date) that saves itself.
   */
  actions?: { dirty: boolean; canSave: boolean };
  children: (close: (keep: boolean) => void) => React.ReactNode;
}

/** The cell and its panel: opens on click, follows the cell, a click outside or Escape closes it. */
const CellPanel = ({ label, resting, width, busy, mark, markStyle, edited = false, reopen, onClose, onOpen, actions, children }: CellPanelProps) => {
  const [open, setOpen] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);
  const position = usePanelPlacement(cellRef, panelRef, open, width, 320);
  const dirtyRef = useRef(false);
  dirtyRef.current = actions?.dirty ?? false;

  const close = (keep: boolean, refocus = true) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setOpen(false);
    onClose(keep);
    if (refocus) focusCellBack(cellRef.current);
  };

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const path = event.composedPath();
      if (path.includes(panelRef.current) || path.includes(cellRef.current) || isInTypeaheadList(path)) return;
      if (dirtyRef.current) holdOutsideClick(event, panelRef.current, cellRef.current);
      else close(!actions, false);
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [open]);

  // Into the panel's first box, so typing and the down arrow work at once - its list no longer opens on focus.
  useEffect(() => {
    if (open && position) (panelRef.current?.querySelector('input') ?? panelRef.current)?.focus();
  }, [open, position != null]);

  const openPanel = () => { closingRef.current = false; onOpen?.(); setOpen(true); };

  useEffect(() => { if (reopen) { closingRef.current = false; setOpen(true); } }, [reopen]);

  return (
    <ProviderConsumer
      render={globalTheme => {
        const marked = markClass(mark, edited);
        const lock = savingCell(busy);
        return (
        <>
          <div
            ref={cellRef}
            className={`inline-edit-cell${marked}${lock.className}`}
            {...lock.props}
            data-selection-disabled={true}
            tabIndex={0}
            role="button"
            aria-label={label}
            aria-expanded={open}
            onMouseDown={stop}
            onClick={event => { stop(event); if (!open) openPanel(); }}
            onKeyDown={event => {
              if (event.key === 'Escape' && open) { stop(event); event.preventDefault(); close(false); return; }
              if (!open && (event.key === 'Enter' || event.key === 'F2')) { stop(event); event.preventDefault(); openPanel(); }
            }}
            style={{
              ...CELL_BOX,
              
              cursor: 'text',
              outline: open ? `1px solid ${globalTheme.siteVariables.colorScheme.brand.foreground}` : undefined,
              outlineOffset: -1, ...markStyle
            }}
          >
            {resting}
          </div>
          {open && position && ReactDOM.createPortal(
            <div
              ref={panelRef}
              {...PANEL_PORTAL}
              role="dialog"
              tabIndex={-1}
              aria-label={label}
              className="rounded"
              onMouseDown={stop}
              onClick={stop}
              // A double-click here reaches the list's SelectionZone through the portal, which climbs from the target for its row,
              // never meets the list and throws on the document (#39).
              onDoubleClick={stop}
              onKeyDown={event => {
                event.stopPropagation();
                const inputs = Array.from(panelRef.current?.querySelectorAll('input') ?? []);
                if (event.key === 'Escape') { event.preventDefault(); close(false); }
                else if (actions && isPanelAction(event.target)) wrapActionTab(event, panelRef.current, inputs);
                else if (event.key === 'Enter') {
                  event.preventDefault();
                  if (!actions || actions.canSave) close(true);
                  else if (!actions.dirty) close(false);
                }
                else if (event.key === 'Tab') {
                  const edge = event.shiftKey ? inputs[0] : inputs[inputs.length - 1];
                  if (actions && event.target === edge) {
                    event.preventDefault();
                    focusActions(panelRef.current, event.shiftKey);
                  } else if (!actions && (!inputs.length || event.target === edge)) { event.preventDefault(); close(true); }
                }
              }}
              style={{
                position: 'fixed',
                left: position.left,
                ...(position.bottom === undefined ? { top: position.top } : { bottom: position.bottom }),
                width,
                zIndex: 1000000,
                outline: 'none',
                padding: 12,
                backgroundColor: globalTheme.siteVariables.colorScheme.default.background,
                // The panel is portalled to the page's body, outside the themed app, so its text takes the theme's colour here.
                color: globalTheme.siteVariables.colorScheme.default.foreground,
                boxShadow: getDropdownBoxShadow(globalTheme),
                ...ThemeColorScheme(globalTheme.siteVariables)
              }}
            >
              {children(keep => close(keep))}
              {actions && (
                <div style={{ marginTop: 12 }}>
                  <PanelActions canSave={actions.canSave} onSave={() => close(true)} onCancel={() => close(false)} />
                </div>
              )}
            </div>,
            document.body
          )}
        </>
        );
      }}
    />
  );
};

interface DateCellProps {
  /** The ticket's (or row's) identity: grid rows are reused by position, so a mark must not outlive its ticket. */
  rowKey: number;
  label: string;
  /**
   * What the cell's save state (value being saved, green box, last-edited box) is kept under, by ticket - the column's
   * name unless given. A custom property passes its id: one named like a built-in column shared that column's state.
   */
  stateKey?: string;
  value: Date | null;
  /** Due and resolution dates can be emptied; a required custom property cannot. */
  clearable: boolean;
  resting: React.ReactElement;
  /** How the list draws a date in this column - the picked one is drawn so while its save is out. */
  restingFor?: (date: Date | null) => React.ReactElement;
  commit: Commit<Date | null>;
}

const shortDate = (date: Date | null) => <span>{date ? ConvertDateToLocale(date.toISOString(), DateTime.DATE_SHORT) : '-'}</span>;

const renderCalendarHeader = (_Component: React.ElementType, props: DatepickerCalendarHeaderProps) => (
  <Flex space="between" hAlign="center">
    <Datepicker.CalendarHeaderAction onClick={props.onPreviousClick} direction="previous" title={props.prevMonthAriaLabel} disabled={props.disabledPreviousButton} />
    <Text content={props.label} styles={{ paddingTop: pxToRem(5) }} />
    <Datepicker.CalendarHeaderAction onClick={props.onNextClick} direction="next" title={props.nextMonthAriaLabel} disabled={props.disabledNextButton} />
  </Flex>
);

/**
 * A date, picked from the ticket page's calendar (Northstar's DatepickerCalendar, the one TicketDatePicker
 * opens) in a panel under the cell. Picking a day saves it; Clear empties it where allowed; Escape or a click
 * away leaves it as it was.
 */
export const InlineDateCell = ({ rowKey, label, stateKey, value, clearable, resting, restingFor = shortDate, commit }: DateCellProps) => {
  const [saving, setSaving] = useState(false);
  const [reopen, setReopen] = useState(0);
  const valueKey = value?.getTime() ?? null;
  const saveMark = useSaveMark(rowKey, valueKey, saving, stateKey ?? label);
  const reload = useGreenOnReload(rowKey, valueKey, saveMark);
  /** The picked date, shown from the pick until the row holds it - the row's own (resting) is the old one. */
  const [pending, setPending] = useState<{ date: Date | null } | null>(null);
  const timers = useCellTimers();
  useEffect(() => { setPending(null); timers.clear(); }, [rowKey, valueKey]);
  const save = async (date: Date | null) => {
    const picked = { date };
    setPending(picked);
    setSaving(true);
    reload.begin();
    const outcome = await commit(date);
    setSaving(false);
    if (outcome === 'saved') {
      reload.wait();
      // A row that comes back unchanged (an automation put the date back) never moves the value; let go anyway.
      timers.later(() => setPending(current => (current === picked ? null : current)), 5000);
      return;
    }
    setPending(null);
    saveMark.show(outcome);
    if (outcome === 'failed') setReopen(n => n + 1);
  };
  return (
    <CellPanel label={label} resting={pending ? restingFor(pending.date) : resting} width={260} busy={saveMark.busy || reload.waiting} mark={saveMark.mark} markStyle={saveMark.markStyle} edited={saveMark.edited} reopen={reopen} onOpen={saveMark.opening} onClose={() => undefined}>
      {close => (
        <Flex column gap="gap.small">
          <Text content={label} style={{ fontSize: 14 }} />
          <DatepickerCalendar
            {...DatepickerLocalizations}
            firstDayOfWeek={0}
            selectedDate={value ?? undefined}
            navigatedDate={value ?? new Date()}
            // As TicketDatePicker: fit the month rather than keep room for six weeks.
            styles={{ minHeight: 'auto' }}
            // The calendar on its own hands back the grid day; its Date is originalDate.
            onDateChange={(_, data) => { close(false); save(data.value.originalDate); }}
            header={{ children: renderCalendarHeader }}
          />
          {clearable && value && (
            <Flex hAlign="end">
              <Button size="small" text icon={<CloseIcon size="small" />} content={i18next.t('time-tracking.clear')} onClick={() => { close(false); save(null); }} />
            </Flex>
          )}
        </Flex>
      )}
    </CellPanel>
  );
};

interface MultiListCellProps {
  rowKey: number;
  label: string;
  /**
   * What the cell's save state (value being saved, green box, last-edited box) is kept under, by ticket - the column's
   * name unless given. A custom property passes its id: one named like a built-in column shared that column's state.
   */
  stateKey?: string;
  options: CategoryOption[];
  valueIds: number[];
  required?: boolean;
  resting: React.ReactElement;
  commit: Commit<number[]>;
  /** Offered when what is typed names no option - see MultiTypeaheadField. */
  onCreate?: (name: string) => Promise<CategoryOption | null>;
  createLabel?: (name: string) => string;
  sortChips?: boolean;
}

/** Several values from a list - a list custom property that allows more than one - in a panel of chips. */
export const InlineMultiListCell = ({ rowKey, label, stateKey, options, valueIds, required, resting, commit, onCreate, createLabel, sortChips }: MultiListCellProps) => {
  const [draft, setDraft] = useState<number[]>(valueIds);
  const [saving, setSaving] = useState(false);
  const [reopen, setReopen] = useState(0);
  const key = valueIds.join(',');
  const saveMark = useSaveMark(rowKey, key, saving, stateKey ?? label);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const reload = useGreenOnReload(rowKey, key, saveMark);
  /**
   * The picks, shown in the cell from Save until the row holds them - the row's own value (resting) is the old one
   * until the save lands, so a blank cell looked as if nothing had been picked while the save was out.
   */
  const [pending, setPending] = useState<number[] | null>(null);
  const timers = useCellTimers();
  useEffect(() => { setPending(null); timers.clear(); }, [rowKey, key]);
  const pendingNames = pending?.map(id => options.find(option => option.id === id)?.name).filter((name): name is string => !!name);
  if (pendingNames && sortChips) pendingNames.sort((a, b) => a.localeCompare(b));
  const shown = pendingNames
    ? <div className="w-full truncate mr-4" {...hoverTitle(pendingNames.join(', '))}><bdi>{pendingNames.join(', ')}</bdi></div>
    : resting;
  const dirty = [...draft].sort((a, b) => a - b).join(',') !== [...valueIds].sort((a, b) => a - b).join(',');
  const canSave = dirty && !(required && !draft.length);

  const onClose = async (keep: boolean) => {
    const picked = draftRef.current;
    if (!keep || !dirty || (required && !picked.length)) return;
    setPending(picked);
    setSaving(true);
    reload.begin();
    const outcome = await commit(picked);
    setSaving(false);
    if (outcome === 'saved') {
      reload.wait();
      // A row that comes back unchanged (an automation put the value back) never moves the key; let go anyway.
      timers.later(() => setPending(current => (current === picked ? null : current)), 5000);
      return;
    }
    setPending(null);
    saveMark.show(outcome);
    // Reopened with the agent's picks still in it (the reopen does not reset the draft; a fresh open does).
    if (outcome === 'failed') setReopen(n => n + 1);
  };

  return (
    <CellPanel label={label} resting={shown} width={300} busy={saveMark.busy || reload.waiting} mark={saveMark.mark} markStyle={saveMark.markStyle} edited={saveMark.edited} reopen={reopen}
      actions={{ dirty, canSave }} onClose={onClose} onOpen={() => { saveMark.opening(); setDraft(valueIds); }}>
      {() => <MultiTypeaheadField label={label} options={options} valueIds={draft} onChange={setDraft} size={12} onCreate={onCreate} createLabel={createLabel} sortChips={sortChips} />}
    </CellPanel>
  );
};

// ---------------------------------------------------------------------------------------------------------------
// A custom property, by its type

interface CustomPropertyCellProps {
  ticketId: number;
  property: ICustomProperty;
  /** The ticket's CustomPropertiesAnswerJson, parsed. */
  answers: Record<string, unknown>;
  resting: React.ReactElement;
  onSaved: () => void | Promise<void>;
  /**
   * Puts the saved answer into the row at once, so a panel (date, multi-list) can show it and go green without
   * waiting for the reload. Without it (My Work) the green waits for the reload.
   */
  onPatch?: (patch: { CustomPropertiesAnswerJson: string }) => void;
  guard?: () => Promise<void>;
}

const BOOL_TRUE = 1;
const BOOL_FALSE = 0;

const boolAnswerId = (raw: unknown): number | null => {
  if (raw == null || raw === '') return null;
  const isTrue = raw === true || (typeof raw === 'string' && raw.toLowerCase() === 'true');
  return isTrue ? BOOL_TRUE : BOOL_FALSE;
};

/**
 * Saves one custom property's answer, merged into the ticket's others on the server - BulkEditTickets merges
 * CustomPropertiesAnswerJson key by key. The ticket page writes the whole set back instead, which could
 * overwrite a change someone else made to another property in the meantime.
 */
const saveCustomProperty = (ticketId: number, propertyId: string) => async (value: unknown) => {
  await new platformService().bulkEditTickets([ticketId], JSON.stringify({ CustomPropertiesAnswerJson: JSON.stringify({ [propertyId]: value }) }), '');
};

/**
 * A custom property's cell, edited by its type as the ticket page edits it (TicketCustomProperties), and saved
 * in the format that page saves: a list as its item ids ("12" or "12,14"), a date as the start of the day with
 * the agent's offset, a time as an ISO time today in UTC, true/false as a boolean.
 */
export const InlineCustomPropertyCell = ({ ticketId, property, answers, resting, onSaved, onPatch, guard }: CustomPropertyCellProps) => {
  const key = property.PropertyId;
  const raw = answers[key];
  const label = property.Name;
  // By the property, not its name - one called "Priority" shared the Priority column's save state.
  const stateKey = `cp:${key}`;
  const required = !!property.IsRequired;
  const save = async (value: unknown) => {
    await saveCustomProperty(ticketId, key)(value);
    onPatch?.({ CustomPropertiesAnswerJson: JSON.stringify({ ...answers, [key]: value }) });
  };
  // As the lists draw a custom property (renderCPRecordValue, My Work's), for a value not saved yet - a time or a
  // date otherwise showed in its box's form ("09:30") and changed to the list's ("9:30 AM") when it landed.
  const restingFor = (stored: unknown) => <CustomPropertyResting answers={{ ...answers, [key]: stored }} property={property} />;
  const listItems = useMemo(
    () => buildCategoryOptions((property.CustomPropertyListItems ?? []).filter(item => !item.IsArchived && !item.IsDeleted) as never),
    [property]
  );

  switch (CustomPropertyDataType_LABELS[property.DataType]) {
    case CustomPropertyDataType_LABELS.STRING:
    case CustomPropertyDataType_LABELS.INT:
      return (
        <InlineTextCell
          rowKey={ticketId}
          value={answerText(raw)}
          inputType={CustomPropertyDataType_LABELS[property.DataType] === CustomPropertyDataType_LABELS.INT ? 'number' : 'text'}
          label={label}
          stateKey={stateKey}
          required={required}
          resting={resting}
          restingFor={restingFor}
          commit={committing<string>(save, onSaved, guard)}
        />
      );
    case CustomPropertyDataType_LABELS.TIME:
      return (
        <InlineTextCell
          rowKey={ticketId}
          value={raw ? formatTimeInput(raw as string) ?? '' : ''}
          inputType="time"
          label={label}
          stateKey={stateKey}
          required={required}
          resting={resting}
          restingFor={time => restingFor(time ? getCardTimeInputISOFormatted(time) : '')}
          commit={committing<string>(time => save(time ? getCardTimeInputISOFormatted(time) : ''), onSaved, guard)}
        />
      );
    case CustomPropertyDataType_LABELS.DATE:
      return (
        <InlineDateCell
          rowKey={ticketId}
          label={label}
          stateKey={stateKey}
          value={raw ? new Date(raw as string) : null}
          clearable={!required}
          resting={resting}
          restingFor={date => restingFor(date ? getCardDateInputISOFormatted(getISODateOnlyPartString(date)) : '')}
          commit={committing<Date | null>(date => save(date ? getCardDateInputISOFormatted(getISODateOnlyPartString(date)) : ''), onSaved, guard)}
        />
      );
    case CustomPropertyDataType_LABELS.BOOL: {
      // A required True/False can only be ticked, as on the ticket page, where false is the box left empty: the cell
      // still shows "False" at rest, but offers True alone (Shaun, 2026-10-20).
      const options: CategoryOption[] = required
        ? [{ id: BOOL_TRUE, name: i18next.t('ticket.child-filter.true') }]
        : [
          { id: BOOL_TRUE, name: i18next.t('ticket.child-filter.true') },
          { id: BOOL_FALSE, name: i18next.t('ticket.child-filter.false') }
        ];
      const answer = boolAnswerId(raw);
      const current = required && answer === BOOL_FALSE ? null : answer;
      return (
        <InlineComboCell
          ticketId={ticketId}
          valueId={current}
          options={options}
          label={label}
          stateKey={stateKey}
          restingTitle={id => optionName(options, id)}
          renderResting={id => (id === current ? resting : <span>{optionName(options, id)}</span>)}
          commit={committing<number>(id => save(id === BOOL_TRUE), onSaved, guard)}
        />
      );
    }
    case CustomPropertyDataType_LABELS.LIST: {
      const ids = answerText(raw).split(',').map(id => Number(id.trim())).filter(id => id > 0);
      // The answer's archived or deleted choices, named and kept rather than shown blank.
      const options = withHeldOptions(listItems, (property.CustomPropertyListItems ?? []) as never, ids);
      if (property.CustomPropertyListConfiguration?.AllowMultiple) {
        return (
          <InlineMultiListCell
            rowKey={ticketId}
            label={label}
            stateKey={stateKey}
            options={options}
            valueIds={ids}
            required={required}
            resting={resting}
            commit={committing<number[]>(picked => save(picked.join(',')), onSaved, guard)}
          />
        );
      }
      const current = ids[0] ?? null;
      return (
        <InlineComboCell
          ticketId={ticketId}
          valueId={current}
          options={options}
          label={label}
          stateKey={stateKey}
          restingTitle={id => optionName(options, id)}
          renderResting={id => (id === current ? resting : <span>{optionName(options, id)}</span>)}
          // An optional property can be emptied, as on the ticket page (Shaun, 2026-10-19); a required one cannot.
          clearable={!required}
          commit={committing<number>(id => save(id == null ? '' : String(id)), onSaved, guard)}
        />
      );
    }
    default:
      return resting;
  }
};
